import Phaser from 'phaser';
import { DataKeys, ScenarioIds } from '../game/Constants';
import type {
  AdvisorData,
  BattleTerrainData,
  BuildingData,
  DoctrineData,
  DynastyData,
  EspionageOperationData,
  EventData,
  FactionData,
  GoodData,
  InstitutionData,
  MissionData,
  ProvinceData,
  ScenarioData,
  SeaZoneData,
  ShipData,
  TechnologyData,
  UnitData,
} from '../types/data';

function indexById<T extends { id: string }>(items: T[] = []): Record<string, T> {
  const out: Record<string, T> = {};
  for (const item of items) out[item.id] = item;
  return out;
}

export class DataRegistry {
  static readonly instance = new DataRegistry();

  factions: Record<string, FactionData> = {};
  provinces: Record<string, ProvinceData> = {};
  goods: Record<string, GoodData> = {};
  buildings: Record<string, BuildingData> = {};
  units: Record<string, UnitData> = {};
  ships: Record<string, ShipData> = {};
  seaZones: Record<string, SeaZoneData> = {};
  events: Record<string, EventData> = {};
  technologies: Record<string, TechnologyData> = {};
  institutions: Record<string, InstitutionData> = {};
  doctrines: Record<string, DoctrineData> = {};
  missions: Record<string, MissionData> = {};
  espionageOperations: Record<string, EspionageOperationData> = {};
  advisors: Record<string, AdvisorData> = {};
  dynasties: Record<string, DynastyData> = {};
  battleTerrain: Record<string, BattleTerrainData> = {};
  scenarios: Record<string, ScenarioData> = {};

  loadFromCache(cache: Phaser.Cache.BaseCache) {
    this.factions = indexById(cache.get(DataKeys.factions) ?? []);
    this.provinces = indexById(cache.get(DataKeys.provinces) ?? []);
    this.goods = indexById(cache.get(DataKeys.goods) ?? []);
    this.buildings = indexById(cache.get(DataKeys.buildings) ?? []);
    this.units = indexById(cache.get(DataKeys.units) ?? []);
    this.ships = indexById(cache.get(DataKeys.ships) ?? []);
    this.seaZones = indexById(cache.get(DataKeys.seaZones) ?? []);
    this.events = indexById(cache.get(DataKeys.events) ?? []);
    this.technologies = indexById(cache.get(DataKeys.technologies) ?? []);
    this.institutions = indexById(cache.get(DataKeys.institutions) ?? []);
    this.doctrines = indexById(cache.get(DataKeys.doctrines) ?? []);
    this.missions = indexById(cache.get(DataKeys.missions) ?? []);
    this.espionageOperations = indexById(cache.get(DataKeys.espionageOperations) ?? []);
    this.advisors = indexById(cache.get(DataKeys.advisors) ?? []);
    this.dynasties = Object.fromEntries((cache.get(DataKeys.dynasties) ?? []).map((dynasty: DynastyData) => [dynasty.faction, dynasty]));
    this.battleTerrain = indexById(cache.get(DataKeys.battleTerrain) ?? []);
    this.scenarios = {};

    for (const scenarioId of ScenarioIds) {
      const scenario = cache.get(`scenario:${scenarioId}`) as ScenarioData | undefined;
      if (scenario) this.scenarios[scenario.id] = scenario;
    }
  }

  factionList() { return Object.values(this.factions); }
  provinceList() { return Object.values(this.provinces); }
  goodList() { return Object.values(this.goods); }
  buildingList() { return Object.values(this.buildings); }
  unitList() { return Object.values(this.units); }
  shipList() { return Object.values(this.ships); }
  seaZoneList() { return Object.values(this.seaZones); }
  scenarioList() { return Object.values(this.scenarios); }
  technologyList() { return Object.values(this.technologies); }
  institutionList() { return Object.values(this.institutions); }
  doctrineList() { return Object.values(this.doctrines); }
  missionList() { return Object.values(this.missions); }
  espionageOperationList() { return Object.values(this.espionageOperations); }
  advisorList() { return Object.values(this.advisors); }
}